/**
 * JotForm embeds — two strategies, chosen server-side by jotform_embed()
 * (app/functions/template-helpers.php).
 *
 * LAZY (main site) — the PHP renders a reserved-height placeholder carrying data-jotform-*
 * and no iframe; `jotformEmbed()` swaps the iframe in via an IntersectionObserver with a
 * generous rootMargin, so a form near the top of the page loads at once and ones further
 * down load as they approach. Keeps the external embed off the critical path.
 *
 *   <div class="jotform-embed" data-jotform-src="…" data-jotform-id="…" data-jotform-height="…"
 *        style="min-height:200px"><noscript><iframe …></noscript></div>
 *
 * EAGER (microsites) — the PHP already emitted the real <iframe> plus the loader overlay, so
 * the preload scanner started the request during parse and nothing here gates it.
 * `jotformEager()` only loads JotForm's auto-resize handler (the sooner that lands, the sooner
 * the form gets its true height) and dismisses the loader on the iframe's load event. The
 * loader also fades itself out in CSS, so it can never outlive a theme.js that failed to run.
 *
 *   <div class="jotform-embed jotform-embed--eager" data-jotform-id="…"><iframe …>
 *        <div class="jotform-embed__loader">…</div></div>
 *
 * `?jotformload=false` (local only) makes the PHP render a static placeholder in either mode,
 * so nothing here matches — used for clean headless screenshots. See CLAUDE.md.
 */

const HANDLER_SRC = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
const FORM_ORIGIN = 'https://form.jotform.com';
// Cross-origin iframes do fire `load`, but a blocked or hung request may never get there —
// drop the loader anyway rather than leave it sitting on top of the form.
const LOADER_FALLBACK_MS = 8000;

let embedHandlerPromise = null;

// Load JotForm's iframe auto-resize handler once (defines window.jotformEmbedHandler).
function loadEmbedHandler() {
  if (window.jotformEmbedHandler) return Promise.resolve();
  if (embedHandlerPromise) return embedHandlerPromise;
  embedHandlerPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = HANDLER_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => resolve(); // the iframe still works without auto-resize
    document.head.appendChild(s);
  });
  return embedHandlerPromise;
}

// Let JotForm size the iframe to the form once the handler is available.
function attachResizeHandler(id) {
  if (!id) return;
  loadEmbedHandler().then(() => {
    if (window.jotformEmbedHandler) {
      window.jotformEmbedHandler(`iframe[id='JotFormIFrame-${id}']`, FORM_ORIGIN);
    }
  });
}

// Fade the loader out, then drop it once the transition has run.
function dismissLoader(loader) {
  if (!loader || loader.dataset.jotformDone) return;
  loader.dataset.jotformDone = '1';
  loader.classList.add('is-hidden');
  setTimeout(() => loader.remove(), 350); // let the fade finish
}

// Hide the loader when the iframe actually has the form — not on a blind timer, which used
// to clear the spinner while the embed was still fetching.
function dismissLoaderWhenReady(iframe, loader) {
  if (!loader) return;
  iframe.addEventListener('load', () => dismissLoader(loader), { once: true });
  setTimeout(() => dismissLoader(loader), LOADER_FALLBACK_MS);
}

// Spinner + label overlay (styles: src/sass/blocks/_jotform.scss).
function buildLoader(text) {
  const loader = document.createElement('div');
  loader.className = 'jotform-embed__loader';
  loader.setAttribute('role', 'status');
  const spinner = document.createElement('span');
  spinner.className = 'jotform-embed__spinner';
  spinner.setAttribute('aria-hidden', 'true');
  const label = document.createElement('span');
  label.className = 'jotform-embed__loading-text';
  label.textContent = text; // textContent → no HTML injection from the translation
  loader.append(spinner, label);
  return loader;
}

function loadForm(el) {
  const src = el.dataset.jotformSrc;
  if (!src || el.dataset.jotformLoaded) return;
  el.dataset.jotformLoaded = '1';

  const id = el.dataset.jotformId || '';
  const height = parseInt(el.dataset.jotformHeight, 10) || 800;
  const loader = buildLoader(el.dataset.jotformLoading || 'Loading form…');

  const iframe = document.createElement('iframe');
  if (id) iframe.id = 'JotFormIFrame-' + id;
  iframe.title = 'JotForm';
  iframe.src = src;
  iframe.setAttribute('allow', 'geolocation; microphone; camera; fullscreen; payment');
  iframe.setAttribute('allowtransparency', 'true');
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('scrolling', 'no');
  iframe.className = 'w-full rounded-xl';
  iframe.style.cssText = `min-width:100%;max-width:100%;height:${height}px;border:none;`;

  // Reserve the form's height so the spinner centers in the final area (no post-load jump),
  // then insert the iframe with the loader overlaid on top.
  el.style.minHeight = height + 'px';
  el.replaceChildren(iframe, loader);

  dismissLoaderWhenReady(iframe, loader);
  attachResizeHandler(id);
}

/**
 * Microsite embeds — the iframe is already in the HTML and already loading. Wire up the
 * resize handler and the loader dismissal. Runs in initCritical: nothing here blocks paint,
 * and on the funnel the form's real height is worth having as early as possible.
 */
export function jotformEager() {
  document.querySelectorAll('.jotform-embed--eager').forEach((el) => {
    if (el.dataset.jotformWired) return;
    el.dataset.jotformWired = '1';

    const iframe = el.querySelector('iframe');
    if (!iframe) return;
    dismissLoaderWhenReady(iframe, el.querySelector('.jotform-embed__loader'));
    attachResizeHandler(el.dataset.jotformId || '');
  });
}

/**
 * Main-site embeds — load each placeholder as it approaches the viewport.
 * Eager embeds carry no data-jotform-src, so this selector never touches them.
 */
export function jotformEmbed() {
  const forms = document.querySelectorAll('.jotform-embed[data-jotform-src]');
  if (!forms.length) return;

  if (!('IntersectionObserver' in window)) {
    forms.forEach(loadForm); // no IO support → just load them all
    return;
  }

  // rootMargin 600px top+bottom = "close enough". A form in/near the initial viewport
  // (e.g. the first/second block) satisfies this on setup and loads immediately.
  const io = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          loadForm(entry.target);
          obs.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '600px 0px' }
  );

  forms.forEach((el) => io.observe(el));
}
